////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. MANDATORY FILE HEADER
// ⚪ [Subsection 1: PURE WHITE] BitsBound Pricing Pod - Orchestration layer for pricing page
// ⚪ [Subsection 2: PURE WHITE] Created: 2026-01-02
// ⚪ [Subsection 3: PURE WHITE] Purpose: Manages pricing state, tier selection, and CTA actions
// ⚪ [Subsection 4: PURE WHITE] Version: 1.0.0 - World-Class Pricing Implementation
// ⚪ [Subsection 5: PURE WHITE] Copyright: © 2026 BitsBound (TayTrom Holdings) - All Rights Reserved

// II. ASCII FUNCTION FLOW DIAGRAM
/*
╔════════════════════════════════════════════════════════════════════════════════════════════╗
║                                PRICING POD ORCHESTRATION                                    ║
║                                                                                             ║
║    User Action → Pod Logic → State Update → Pea Render → Visual Feedback                   ║
║         ↓            ↓           ↓             ↓               ↓                           ║
║    [Click]     [Handler]    [useState]    [Props]        [Animation]                       ║
║                                                                                             ║
║    RESPONSIBILITIES:                                                                        ║
║    ├─ Pricing tier state management                                                         ║
║    ├─ Billing period toggle (monthly/annual)                                               ║
║    ├─ CTA navigation handling                                                              ║
║    ├─ Feature comparison logic                                                             ║
║    └─ Analytics event tracking                                                             ║
╚════════════════════════════════════════════════════════════════════════════════════════════╝
*/

// III. IMPORTS
import React, { useState, useCallback } from 'react';
import PricingPea from '../pea/Bitsbound_Kings_Home_Primary_SRC_Pricing_Lobby_Pea.js';

// IV. INDEPENDENT CONSTANTS
// 🟦 [Subsection 10: BRIGHT TEAL] Auth URL detection for environment-aware routing
const getAuthBaseUrl = (): string => {
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        if (hostname === 'bitsbound.com' || hostname === 'www.bitsbound.com') {
            return '';
        }
    }
    return import.meta.env.VITE_AUTH_BASE_URL || 'http://127.0.0.1:2101';
};
const AUTH_BASE_URL = getAuthBaseUrl();

// 🟦 [Subsection 11: BRIGHT TEAL] TypeScript interfaces for pricing structure
export interface PricingTier {
    id: string;
    name: string;
    description: string;
    monthlyPrice: number;
    annualPrice: number;
    credits: number;
    creditsFormatted: string;
    pricePerMillionCredits: number;
    features: string[];
    highlighted: boolean;
    ctaText: string;
    badge?: string;
}

export interface PricingFaq {
    question: string;
    answer: string;
}

// 🟦 [Subsection 12: BRIGHT TEAL] Pricing tiers data - from CLAUDE.md authoritative source
const PRICING_TIERS: PricingTier[] = [
    {
        id: 'tier_one',
        name: 'Tier One',
        description: 'Perfect for solo attorneys and small practices getting started with AI contract analysis.',
        monthlyPrice: 30,
        annualPrice: 300, // 2 months free
        credits: 1_000_000,
        creditsFormatted: '1M',
        pricePerMillionCredits: 30,
        features: [
            '1 million credits per month',
            'All 9 contract verticals',
            'Real OOXML Track Changes',
            'Negotiation email drafts',
            'Risk analysis dashboard',
            'Playbook customization',
            'Email support',
        ],
        highlighted: false,
        ctaText: 'Start Free Trial',
    },
    {
        id: 'tier_two',
        name: 'Tier Two',
        description: 'Ideal for busy attorneys handling multiple contracts weekly.',
        monthlyPrice: 150,
        annualPrice: 1500, // 2 months free
        credits: 5_000_000,
        creditsFormatted: '5M',
        pricePerMillionCredits: 30,
        features: [
            '5 million credits per month',
            'All 9 contract verticals',
            'Real OOXML Track Changes',
            'Negotiation email drafts',
            'Risk analysis dashboard',
            'Playbook customization',
            'Priority email support',
        ],
        highlighted: true,
        ctaText: 'Start Free Trial',
        badge: 'Recommended Start',
    },
    {
        id: 'tier_three',
        name: 'Tier Three',
        description: 'For legal departments that need to scale contract review capacity.',
        monthlyPrice: 750,
        annualPrice: 7500, // 2 months free
        credits: 25_000_000,
        creditsFormatted: '25M',
        pricePerMillionCredits: 30,
        features: [
            '25 million credits per month',
            'All 9 contract verticals',
            'Real OOXML Track Changes',
            'Negotiation email drafts',
            'Risk analysis dashboard',
            'Playbook customization',
            'Priority email support',
        ],
        highlighted: false,
        ctaText: 'Start Free Trial',
    },
    {
        id: 'tier_four',
        name: 'Tier Four',
        description: 'For large legal departments and firms with high-volume needs.',
        monthlyPrice: 4500,
        annualPrice: 45000, // 2 months free
        credits: 150_000_000,
        creditsFormatted: '150M',
        pricePerMillionCredits: 30,
        features: [
            '150 million credits per month',
            'All 9 contract verticals',
            'Real OOXML Track Changes',
            'Negotiation email drafts',
            'Risk analysis dashboard',
            'Playbook customization',
            'Dedicated account manager',
        ],
        highlighted: false,
        ctaText: 'Contact Sales',
    },
];

// 🟦 [Subsection 13: BRIGHT TEAL] FAQ data
const PRICING_FAQS: PricingFaq[] = [
    {
        question: 'What are credits and how are they consumed?',
        answer: 'Credits represent AI processing capacity (1 credit = 1 token). Consumption varies based on contract complexity, length, and analysis depth—from less than 1 million for a simple NDA draft to ~30 million for a complex 70-page commercial lease redline with high aggressiveness. Your first contract always completes free regardless of consumption, so you can experience the full product before subscribing.',
    },
    {
        question: 'Do unused credits roll over?',
        answer: 'Yes! Unused credits roll over indefinitely with no cap. Accumulate as many credits as you want. You must maintain an active subscription to keep your credits—if you cancel, remaining credits are forfeited.',
    },
    {
        question: 'What happens if I run out of credits?',
        answer: 'You can upgrade your plan at any time to get more credits immediately. Upgrades are prorated, so you only pay for the remaining time in your billing cycle.',
    },
    {
        question: 'Can I try BitsBound before subscribing?',
        answer: 'Yes! Your first contract analysis always completes free—regardless of complexity or token consumption. Even a 70-page redline that would normally cost $900 runs to completion on your first try. No credit card required.',
    },
    {
        question: 'What contract types does BitsBound support?',
        answer: 'BitsBound supports 9 contract verticals: SaaS/MSA, M&A (APA, SPA, Merger), VC (SAFE, Term Sheets), Employment, NDA, Equipment Lease/Financing, Commercial Lease, Professional Services, and a Miscellaneous catch-all for other contract types.',
    },
    {
        question: 'What do I get with each analysis?',
        answer: 'In Redline Mode (reviewing counterparty paper): (1) a redlined Word document with real Track Changes, (2) a draft negotiation email explaining each change, and (3) an analytical dashboard with risk scores and favorability ratings. In Draft Mode (generating your own paper): a clean Word document ready to send, with optional negotiation email.',
    },
    {
        question: 'Is my data secure?',
        answer: 'Absolutely. BitsBound uses enterprise-grade security: all data is encrypted in transit (TLS 1.3) and at rest (AES-256). We use Google OAuth 2.0 and Microsoft Authenticator TOTP 2FA. Your contracts are processed and then deleted — we don\'t train AI on your data.',
    },
    {
        question: 'Can I cancel anytime?',
        answer: 'Yes, you can cancel your subscription at any time. You\'ll retain access until the end of your current billing period. No long-term commitments required.',
    },
];

// V. FUNCTION/CLASS NAME
/**
 * PricingPod - Orchestration layer for the pricing page
 *
 * Manages all business logic for pricing display and user interactions.
 * Passes computed props to PricingPea for presentation.
 */
const PricingPod: React.FC = () => {
    // VI. DECLARATIONS
    // 🟣 [Subsection 21: VIOLET] State for FAQ accordion
    const [expandedFaqIndex, setExpandedFaqIndex] = useState<number | null>(null);

    // VIII. OPERATIONS

    // 🔴 [Subsection 41: RED] FAQ toggle handler
    const handleFaqToggle = useCallback((index: number) => {
        setExpandedFaqIndex(prev => prev === index ? null : index);
    }, []);

    // 🔴 [Subsection 42: RED] CTA click handler
    // T_TIER_RENAME_2026_01_02: Updated enterprise -> tier_four
    const handleCtaClick = useCallback((tier: PricingTier) => {
        if (tier.id === 'tier_four') {
            // Tier Four (Enterprise-level) -> Contact sales email
            window.location.href = 'mailto:admin@bitsbound.com?subject=BitsBound%20Tier%20Four%20Inquiry&body=Hi%20BitsBound%20Team%2C%0A%0AI\'m%20interested%20in%20learning%20more%20about%20BitsBound%20Tier%20Four.%0A%0ACompany%3A%20%0AEstimated%20Monthly%20Contract%20Volume%3A%20%0AUse%20Case%3A%20%0A%0AThank%20you!';
        } else {
            // Tier One/Two/Three -> Register with tier preselected
            window.location.href = `${AUTH_BASE_URL}/auth/register?plan=${tier.id}`;
        }
    }, []);

    // 🔴 [Subsection 43: RED] Navigate to homepage
    const handleBackToHome = useCallback(() => {
        window.location.href = '/';
    }, []);

    // IX. PRE-OUTPUT PROCESSING
    // 🟡 [Subsection 50: YELLOW] Prepare props for Pea component
    const peaProps = {
        tiers: PRICING_TIERS,
        faqs: PRICING_FAQS,
        expandedFaqIndex,
        onFaqToggle: handleFaqToggle,
        onCtaClick: handleCtaClick,
        onBackToHome: handleBackToHome,
        authBaseUrl: AUTH_BASE_URL,
    };

    // X. OUTPUT
    return <PricingPea {...peaProps} />;
};

export default PricingPod;